/**
 * Browser-compatible OBJ parser.
 * Extracts positions and normals from Wavefront OBJ format.
 * Ignores materials (.mtl) — Osteo3d bone material is applied instead.
 */

export function parseOBJ(text) {
  const vertices = []  // v x y z
  const vnormals = []  // vn x y z
  const posArray = []
  const normArray = []

  const lines = text.split('\n')

  for (const raw of lines) {
    const line = raw.trim()
    if (!line || line.startsWith('#')) continue

    const parts = line.split(/\s+/)
    const keyword = parts[0]

    if (keyword === 'v') {
      vertices.push(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]))
    } else if (keyword === 'vn') {
      vnormals.push(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]))
    } else if (keyword === 'f') {
      // Face: f v1/vt1/vn1 v2/vt2/vn2 v3/vt3/vn3 [v4/vt4/vn4 ...]
      // Triangulate n-gon faces using fan triangulation
      const faceVerts = []
      for (let i = 1; i < parts.length; i++) {
        const indices = parts[i].split('/')
        const vi = parseInt(indices[0])
        const ni = indices.length >= 3 && indices[2] ? parseInt(indices[2]) : 0
        faceVerts.push({ vi, ni })
      }

      // Fan triangulation: vertex 0 is the hub
      for (let i = 1; i < faceVerts.length - 1; i++) {
        const tri = [faceVerts[0], faceVerts[i], faceVerts[i + 1]]
        for (const { vi, ni } of tri) {
          // OBJ indices are 1-based, can be negative (relative)
          const vIdx = vi > 0 ? (vi - 1) * 3 : (vertices.length / 3 + vi) * 3
          posArray.push(vertices[vIdx], vertices[vIdx + 1], vertices[vIdx + 2])

          if (ni !== 0 && vnormals.length > 0) {
            const nIdx = ni > 0 ? (ni - 1) * 3 : (vnormals.length / 3 + ni) * 3
            normArray.push(vnormals[nIdx], vnormals[nIdx + 1], vnormals[nIdx + 2])
          }
        }
      }
    }
  }

  const positions = new Float32Array(posArray)
  let normals

  if (normArray.length === posArray.length) {
    normals = new Float32Array(normArray)
  } else {
    // Compute face normals if OBJ didn't provide them
    normals = computeFaceNormals(positions)
  }

  return {
    positions,
    normals,
    faceCount: positions.length / 9,
  }
}

/**
 * Compute flat face normals from triangle positions.
 */
function computeFaceNormals(positions) {
  const normals = new Float32Array(positions.length)
  const faceCount = positions.length / 9

  for (let i = 0; i < faceCount; i++) {
    const off = i * 9
    // Edge vectors
    const e1x = positions[off + 3] - positions[off]
    const e1y = positions[off + 4] - positions[off + 1]
    const e1z = positions[off + 5] - positions[off + 2]
    const e2x = positions[off + 6] - positions[off]
    const e2y = positions[off + 7] - positions[off + 1]
    const e2z = positions[off + 8] - positions[off + 2]

    // Cross product
    let nx = e1y * e2z - e1z * e2y
    let ny = e1z * e2x - e1x * e2z
    let nz = e1x * e2y - e1y * e2x

    // Normalize
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
    if (len > 1e-10) {
      nx /= len; ny /= len; nz /= len
    }

    // Same normal for all 3 vertices of the face
    for (let v = 0; v < 3; v++) {
      normals[off + v * 3]     = nx
      normals[off + v * 3 + 1] = ny
      normals[off + v * 3 + 2] = nz
    }
  }

  return normals
}
